import { BehaviorSubject, merge, timer, Observable, Subject } from 'rxjs';
import { FeatureCollection, Point, Geometry } from 'geojson';
import { MapElement } from '../map-element.model';
import { map, shareReplay, publishReplay } from 'rxjs/operators';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import { TranslateService } from '@ngx-translate/core';
import { BaseLayer } from './base-layer.model';
import { createMapboxLayer, createFillLayer, createLineLayer, createSymbolLayer, getClickedPointForFeature, applyFilter, searchFromFeatures as searchFromFeatureList } from './utils/map.utils';
import { feature, featureCollection, point } from '@turf/turf';
import { ILayer, LayerStyle, LayerMetadata, LayerFilter, ILayerFunctionality, MapboxLayerType, MapboxEventListener, LayerSearchOptions, LayerSearchResult } from './layer.type';

export type Layer = HTMLMarkerLayer | CanvasLayer;

interface FeatureFromMapbox {
  id: string | number,
  layer: string,
  properties: any,
  type: string,
  geometry: any,
}

export class HTMLMarkerLayer extends BaseLayer implements ILayer, ILayerFunctionality {
  id: string;
  auxiliaryIds: Set<string> = new Set();
  name: string;
  title?: string;
  style?: { layerType?: 'html-marker'; sourceType?: 'geojson'; } & LayerStyle;
  asMarkers?: true;
  source?: string;
  selectable?: boolean;
  data?: MapElement<Point>[];
  metadata: LayerMetadata;
  searchOptions: LayerSearchOptions;
  minZoom?: number;
  useWorker?: true;
  filter?: LayerFilter;
  geojsonURL: string;
  orderingWeight?: number;

  protected readonly mapRef: mapboxgl.Map;
  /**
   * Emit data as markers$ for html-marker -layers
   */
  private readonly featuresBS = new BehaviorSubject<MapElement<Point>[]>([]);
  public readonly features$ = this.featuresBS.asObservable();

  protected readonly isVisibleBS = new BehaviorSubject(false);
  public readonly isVisible$ = this.isVisibleBS.asObservable();

  private readonly isLoadedBS = new BehaviorSubject(false);
  public readonly isLoaded$ = merge(
    this.isLoadedBS,
    // Timeout for showing the "loading" state
    timer(60 * 1000).pipe(map(() => true), shareReplay(1))
  );

  constructor(
    mapRef: mapboxgl.Map,
    layerDefinition: ILayer,
  ) {
    super();
    this.mapRef = mapRef;
    // TODO: Try typing
    // Create Layer from layerDefinition
    for (const key in layerDefinition) {
      this[key] = layerDefinition[key];
    }
  }

  public getFeatures() {
    return this.featuresBS.getValue();
  }

  // Split this to initGeojsonLayer / initVectorLayer. Possibly add type guards for same method?
  public async initLayer(geojson: FeatureCollection<Geometry>) {
    // Verify that layer isn't already added on the map
    const layerFoundOnMap: mapboxgl.Layer = this.mapRef.getLayer(this.id);
    if (!layerFoundOnMap) {
      await this.createGeojsonLayer(geojson);
      this.addHTMLMarkersFromGeoJson(geojson);
    }
  }

  /**
   * Creates a layer with geojson source
   */
  private async createGeojsonLayer(geojson: FeatureCollection<Geometry>): Promise<mapboxgl.Layer> {

    const sourceData: { attribution?: string } = {};
    if (this.metadata?.attribution) {
      sourceData.attribution = this.metadata.attribution;
    }

    /**
     * Add to observable to make the data accessible (for search etc.)
     */
    if (geojson) {
      this.featuresBS.next(geojson.features.map(feature =>
        new MapElement<Point>(
          feature.id ? feature.id : feature.properties[this.metadata.uniqueBy],
          this,
          feature.properties,
          feature.type,
          feature.geometry)
      ));
    }

    return createMapboxLayer(this);
  }

  async addHTMLMarkersFromGeoJson(geojson: FeatureCollection) {
    try {
      if (geojson) {
        const markers: MapElement<Point>[] = geojson.features.map(feature =>
          new MapElement<Point>(
            feature.properties.id,
            this,
            feature.properties,
            feature.type,
            feature.geometry));
        this.pushFeatures(markers);
      }
    } catch (e) {
      console.error(e);
    }
  }

  public pushFeatures(data: MapElement<Point>[]) {
    this.setLoadedStatus(true);
    this.featuresBS.next(data);
  }

  public pushGeojson(data: FeatureCollection<Point>) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      source.setData(data);
      const mapElements: MapElement<Point>[] = data.features.map(f => new MapElement<Point>(f.id, this, f.properties, f.type, f.geometry));
      this.featuresBS.next(mapElements);
    } else {
      console.error('Trying to push geojson to', this.id, 'layer');
    }
  }

  public toggleVisibility() {
    this.setVisibility(!this.isVisibleBS.getValue());
  }

  public setLoadedStatus(isLoaded: boolean) {
    this.isLoadedBS.next(isLoaded);
  }

  public searchFromFeatures(searchTerm: string): LayerSearchResult[] {
    return searchFromFeatureList(searchTerm, this.searchOptions, this.featuresBS.getValue());
  }

  public getSingleFeature$(featureId: string): Observable<MapElement<Point>> {
    return this.features$.pipe(
      map(features => features.find(f => f.id === featureId)),
      publishReplay(1)
    );
  }

  public getSingleFeature(featureId: string | number): MapElement<Point> {
    // weak comparison since element.id is potentially a number
    // tslint:disable-next-line
    return this.featuresBS.getValue().find(feature => feature.id == featureId);
  }
}

export class CanvasLayer extends BaseLayer implements ILayer, ILayerFunctionality {
  id: string;
  auxiliaryIds: Set<string> = new Set();
  name: string;
  title?: string;
  style?: { layerType?: MapboxLayerType; } & LayerStyle;
  asMarkers?: false;
  source?: string;
  selectable?: boolean;
  data?: MapElement<Geometry>[];
  metadata: LayerMetadata;
  searchOptions: LayerSearchOptions;
  minZoom?: number;
  useWorker?: true;
  filter?: LayerFilter;
  geojsonURL?: string;
  vectorURL?: string;
  vectorSourceLayer?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  styleFunction?: any;
  orderingWeight?: number;
  belowRoadLayers?: boolean;
  belowTitles?: boolean;
  fromExternal?: boolean;
  mapboxOptions?: {
    layout?: mapboxgl.AnyLayout;
    paint?: mapboxgl.AnyPaint;
  };

  protected readonly mapRef: mapboxgl.Map;

  private readonly featuresBS = new BehaviorSubject<MapElement<Geometry>[]>([]);
  public readonly features$ = this.featuresBS.asObservable();

  protected readonly isVisibleBS = new BehaviorSubject(false);
  public readonly isVisible$ = this.isVisibleBS.asObservable();

  private readonly isLoadedBS = new BehaviorSubject(false);
  public readonly isLoaded$ = merge(
    this.isLoadedBS,
    // Timeout for showing the "loading" state
    timer(60 * 1000).pipe(map(() => true), shareReplay(1))
  );

  private readonly featureClicked = new Subject<{
    clickedFeature: FeatureFromMapbox,
    event: {
      type: 'click' | 'dblclick',
      originalEvent: {
        clientX: number,
        clientY: number
      }
    }
  }>();
  public readonly featureClicked$ = this.featureClicked.asObservable();

  private readonly isHoveredBS = new BehaviorSubject<boolean>(false);
  public readonly isHovered$ = this.isHoveredBS.asObservable();

  public clicksUnsubscribe = new Subject();
  private eventHandlers: MapboxEventListener[] = [];
  private popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  constructor(
    mapRef: mapboxgl.Map,
    layerDefinition: ILayer,
    private translate: TranslateService
  ) {
    super();
    this.mapRef = mapRef;
    // Create Layer from layerDefinition
    for (const key in layerDefinition) {
      this[key] = layerDefinition[key];
    }
  }

  public getFeatures() {
    return this.featuresBS.getValue();
  }

  // Split this to initGeojsonLayer / initVectorLayer. Possibly add type guards for same method?
  public async initLayer(beforeLayerId: string, geojson: FeatureCollection) {
    this.clearEventListeners();

    const layerFoundOnMap: mapboxgl.Layer = this.mapRef.getLayer(this.id);
    if (!layerFoundOnMap) { // Verify that layer isn't already added on the map
      if (['line', 'symbol', 'fill'].includes(this.style.layerType)) {
        let mapboxLayers: mapboxgl.Layer[] = [];
        switch (this.style.sourceType) {
          case 'geojson':
            mapboxLayers = await this.createGeojsonLayer(geojson);
            break;
          case 'vector':
            try {
              mapboxLayers = await this.createVectorLayer(this.vectorURL);
            } catch (err) {
              console.error(err);
            }
            break;
          default:
            console.log('Source type not defined for', this.id);
        }

        this.setLoadedStatus(true);

        // iterate in reverse order, because `beforeLayerId` flips the final order
        for (const mapboxLayer of mapboxLayers.reverse()) {
          mapboxLayer.minzoom = this.metadata?.visibleByZoom || 0;

          console.log('Add layer', mapboxLayers);
          this.mapRef.addLayer(mapboxLayer, beforeLayerId);

          if (mapboxLayer.id !== this.id) {
            this.auxiliaryIds.add(mapboxLayer.id);
          }
        }

        // Set filters for geojson data
        if (this.filter) {
          for (const mapboxLayer of mapboxLayers) {
            this.mapRef.setFilter(mapboxLayer.id, [
              this.filter.mode === 'OR' ? 'any' : 'all',
              ...this.filter.filters.map(filter => [
                filter.mode === 'exclusive' ? '!in' : 'in',
                filter.field,
                ...(Array.isArray(filter.value) ? filter.value : [filter.value]),
              ])
            ]);
          }
        }

      } else {
        console.log('Layer for websocket data');
      }
    }

    // Click handler for the layer (used to open hover)
    // Add a new event listener only when the layer is loaded first time.
    // Mapbox does not remove layer event listeners when style is changed and layers are reloaded.
    // if (layer.metadata.hoverComponent && layer.isLoaded.getValue()) {
    if (this.metadata.hoverComponent) {
      this.addMapEventListener({ event: 'click', layerId: this.id, listener: this.onLayerClick.bind(this) });
      this.addMapEventListener({ event: 'dblclick', layerId: this.id, listener: this.onLayerClick.bind(this) });
      // Change the cursor to a pointer when the mouse is over the layer.
      this.addMapEventListener({ event: 'mouseenter', layerId: this.id, listener: this.onMouseEnterFeature.bind(this) });
      // Change it back to a pointer when it leaves.
      this.addMapEventListener({ event: 'mouseleave', layerId: this.id, listener: this.onMouseLeaveFeature.bind(this) });
      // Disable context menu
      this.addMapEventListener({ event: 'contextmenu', layerId: this.id, listener: this.onContextMenu.bind(this) });

      // for URL description hovers
      if (this.metadata.url && this.metadata.urlDescription) {
        this.addMapEventListener({ event: 'mouseenter', layerId: this.id, listener: (e) => this.urlPopupShow(e) });
        this.addMapEventListener({ event: 'mouseleave', layerId: this.id, listener: () => this.urlPopupHide() });
      }
    }
  }

  urlPopupShow(e: any) {
    const coordinates = e.features[0].geometry.coordinates.slice();
    const description = this.translate.instant(this.metadata.urlDescription);

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    this.popup.setLngLat(coordinates).setHTML(description).addTo(this.mapRef);
  }

  urlPopupHide() {
    this.popup.remove();
  }

  addMapEventListener(ev: MapboxEventListener) {
    this.mapRef.on(ev.event, ev.layerId, ev.listener);
    this.eventHandlers.push(ev);
  }

  clearEventListeners() {
    this.clicksUnsubscribe.next();
    while (this.eventHandlers.length > 0) {
      const ev = this.eventHandlers.pop();
      this.mapRef.off(ev.event, ev.layerId, ev.listener);
    }
  }

  onMouseEnterFeature() {
    this.isHoveredBS.next(true);
  }

  onMouseLeaveFeature() {
    this.isHoveredBS.next(false);
  }

  onContextMenu(event) {
    event.preventDefault();
  }

  /**
   * Handler for layer click and dblclick events
   */
  private onLayerClick(e: mapboxgl.MapMouseEvent) {
    e.preventDefault();
    e.originalEvent.stopPropagation();

    const resolveClick = ([feature, ...restFeatures]: mapboxgl.MapboxGeoJSONFeature[]) => {
      // Get top layer of the matched stack
      if (!feature || feature.source === 'composite' || !this.metadata.hoverComponent) { // End handler if empty or not custom layer
        if (restFeatures.length > 0) {
          resolveClick(restFeatures);
        }
        return;
      } else {
        if (this.metadata.url) {
          window.open(this.metadata.url + feature.properties[this.metadata.urlProperty], 'blank');
        } else {
          const clickedPoint = point([e.lngLat.lng, e.lngLat.lat]);
          const geometry = getClickedPointForFeature(feature.geometry, clickedPoint);

          const featureId = feature.properties[feature.properties.uniqueBy]
            || `${feature.layer.id} (${geometry.coordinates[0].toPrecision(6)}, ${geometry.coordinates[1].toPrecision(6)})`;
          const featureProps = { ...feature.properties, [feature.properties.uniqueBy]: featureId };
          const clickedFeature: FeatureFromMapbox = {
            ...new MapElement<Point>(featureId, null, featureProps, '', geometry),
            layer: feature.layer.id
          };
          console.log('Click original feature:', feature, 'clicked point:', clickedFeature);
          this.featureClicked.next({
            clickedFeature,
            event: {
              // e.type is always 'click' or 'dbclick' because this function is only bound to those events
              type: e.type as ('click' | 'dblclick'),
              originalEvent: {
                clientX: e.point.x,
                clientY: e.point.y,
              }
            }
          });
        }
      }
    };

    const features = this.mapRef.queryRenderedFeatures(e.point);
    resolveClick(features);
  }

  /**
   * Creates a layer with vector source
   */
  private async createVectorLayer(vectorURL: string): Promise<mapboxgl.Layer[]> {
    if (!this.isSourceAdded(this.id)) {
      if (vectorURL) {
        try {
          this.createVectorSource(vectorURL);
        } catch (err) {
          console.error(err);
        }
      } else {
        throw new Error('No source url for ' + this.id);
      }
    }

    let mapboxLayers: mapboxgl.Layer[] = [];
    // Layer type specific setup
    switch (this.style.layerType) {
      case 'symbol': // Load images for symbol layers
        const imageId = await this.addImage();
        mapboxLayers = createSymbolLayer(this, imageId);
        break;
      case 'line':
        mapboxLayers = createLineLayer(this);
        break;
      case 'fill':
        mapboxLayers = createFillLayer(this);
        break;
      default:
        console.error('Specified layer type was', this.style.layerType);
    }

    for (const mapboxLayer of mapboxLayers) {
      mapboxLayer['source-layer'] = this.vectorSourceLayer;
    }

    return mapboxLayers;
  }

  /**
   * Creates a layer with geojson source
   */
  private async createGeojsonLayer(geojson: FeatureCollection<Geometry>): Promise<mapboxgl.Layer[]> {
    /**
     * Add to observable to make the data accessible (for search etc.)
     */
    if (geojson) {
      this.featuresBS.next(applyFilter(this.filter, geojson.features.map(feature =>
        new MapElement<Point>(
          feature.id ? feature.id : feature.properties[this.metadata.uniqueBy],
          this,
          feature.properties,
          feature.type,
          feature.geometry)
      )));
    }

    const sourceData: mapboxgl.GeoJSONSourceOptions = {};
    if (this.metadata?.attribution) {
      sourceData.attribution = this.metadata.attribution;
    }
    if (this.metadata?.uniqueBy) {
      sourceData.promoteId = this.metadata.uniqueBy;
    }

    if (this.geojsonURL) {
      this.createGeojsonSource(geojson, sourceData);
    } else { // Add geojson source with no initial data
      const emptyGeojson: FeatureCollection<Geometry> = {
        type: 'FeatureCollection',
        features: []
      };
      this.createGeojsonSource(emptyGeojson, sourceData);
    }

    // Layer type specific setup
    switch (this.style.layerType) {
      case 'symbol': // Load images for symbol layers
        const imageId = await this.addImage();
        return createSymbolLayer(this, imageId);
      case 'line':
        return createLineLayer(this);
      case 'fill':
        return createFillLayer(this);
      default:
        console.error('Specified layer type was', this.style.layerType);
    }
  }

  private async createGeojsonSource(geojsonData: FeatureCollection<Geometry>, sourceData: mapboxgl.GeoJSONSourceOptions) {
    if (!this.isSourceAdded(this.id)) {
      this.mapRef.addSource(this.id, {
        type: 'geojson',
        data: geojsonData,
        ...sourceData
      });
    }
  }

  private async addImage(): Promise<string> {
    if (!this.style.icon?.url) {
      return undefined;
    }
    return await new Promise((resolve, reject) => {
      this.mapRef.loadImage(
        this.style.icon?.url,
        (error, image) => {
          if (error) {
            reject(error);
          } else if (!this.mapRef.hasImage(this.id)) {
            this.mapRef.addImage(this.id, image, { sdf: !!this.style.icon?.colorable });
          }
          resolve(this.id);
        }
      );
    });
  }

  private createVectorSource(vectorURL: string) {
    const vectorSource: mapboxgl.VectorSource = {
      type: 'vector',
      url: vectorURL
    };
    if (this.metadata?.attribution) {
      vectorSource.attribution = this.metadata.attribution;
    }
    this.mapRef.addSource(this.id, {
      type: 'vector',
      url: vectorSource.url,
      ...vectorSource
    });
  }

  private isSourceAdded(sourceId: string): boolean {
    return !!this.mapRef.getSource(sourceId);
  }

  async addHTMLMarkersFromGeoJson(geojson: FeatureCollection) {
    try {
      if (geojson) {
        const markers: MapElement<Point>[] = geojson.features.map(feature =>
          new MapElement<Point>(
            feature.properties[this.metadata.uniqueBy],
            this,
            feature.properties,
            feature.type,
            feature.geometry));
        this.pushFeatures(markers);
      }
    } catch (e) {
      console.error(e);
    }
  }

  public pushFeatures(data: MapElement<Point>[]) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      const features = data.map(me => {
        const featureProps = {
          ...me.properties,
          layer: this.id,
          uniqueBy: this.metadata.uniqueBy
        };
        return feature(me.geometry, featureProps)
      });
      const geojson = featureCollection(features);
      try {
        source.setData(geojson as FeatureCollection);
      } catch (err) {
        console.error(err);
        console.warn(geojson, features);
      }
      this.featuresBS.next(applyFilter(this.filter, data));
    } else {
      console.error('Trying to push geojson to', this.id, 'layer with type', source?.type);
    }
  }

  public pushGeojson(data: FeatureCollection<Geometry>) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      source.setData(data);
      const mapElements = data.features.map(f => new MapElement(f.id, this, f.properties, f.type, f.geometry));
      this.featuresBS.next(applyFilter(this.filter, mapElements));
    } else {
      console.error('Trying to push geojson to', this.id, 'layer. Source:', source);
    }
  }

  public toggleVisibility() {
    this.setVisibility(!this.isVisibleBS.getValue());
  }

  public setLoadedStatus(isLoaded: boolean) {
    this.isLoadedBS.next(isLoaded);
  }

  public searchFromFeatures(searchTerm: string): LayerSearchResult[] {
    return searchFromFeatureList(searchTerm, this.searchOptions, this.featuresBS.getValue());
  }

  public getSingleFeature$(featureId: string): Observable<MapElement<Geometry>> {
    return this.features$.pipe(
      map(features => features.find(f => f.id === featureId)),
      publishReplay(1)
    );
  }

  public getSingleFeature(featureId: string | number): MapElement<Geometry> {
    return this.featuresBS.getValue().find(f => f.id === featureId);
  }
}
